
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { listProspects } from "@/api/prospects.api.js";
import {
  listConversationMessages,
  sendConversationMessage
} from "@/api/conversations.api.js";
import { uploadChatMedia, getContentMediaStatus } from "@/api/ghlContent.api.js";
import { resumeGhlAiConversation } from "@/api/ghlAi.api.js";
import { buildGhlContactUrl } from "@/utils/ghlAppUrl.js";
import { createInboxSocket } from "@/utils/inboxSocket.js";
import {
  escapeHtml,
  messageKey as buildMessageKey
} from "@/utils/inboxMessageFormat.js";
import ProspectConversationRow from "@/components/prospects/ProspectConversationRow.vue";
import InboxMessageBubble from "@/components/prospects/InboxMessageBubble.vue";

interface ScoreBreakdown {
  fit?: number;
  engagement?: number;
  intent?: number;
  reasons?: string[];
}

interface InboxAttachment {
  url: string;
  mimeType?: string;
  filename?: string;
}

interface InboxMessage {
  id?: number | string | null;
  role: string;
  content: string;
  messageId?: string | null;
  attachments?: InboxAttachment[];
  createdAt?: string | null;
}

interface ChatAttachmentDraft {
  id: number;
  originalFilename: string;
  mimeType: string;
  publicUrl?: string | null;
  status: string;
}

interface Prospect {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  source?: string;
  humanHandoff?: boolean;
  aiEnabled?: boolean;
  conversationSummary?: string | null;
  summaryGeneratedAt?: string | null;
  conversationId?: string | null;
  lastMessage?: string | null;
  lastMessageAt?: string | null;
  lastMessageRole?: string | null;
  messageChannel?: string | null;
  socialPlatform?: string | null;
  leadScore?: number;
  leadGrade?: string;
  scoreBreakdown?: ScoreBreakdown | null;
  scoredAt?: string | null;
}

type GradeFilter = "" | "hot" | "warm" | "cold";

type InboxSocketHandle = {
  close: () => void;
};

export default defineComponent({
  name: "prospects-list",
  components: {
    ProspectConversationRow,
    InboxMessageBubble
  },
  props: {
    embedded: {
      type: Boolean,
      default: true
    },
    hasGhlLocation: {
      type: Boolean,
      default: false
    },
    locationId: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      loading: true,
      searching: false,
      syncing: false,
      resumingId: "",
      loadError: "",
      threadLoading: false,
      threadError: "",
      sendingMessage: false,
      searchQuery: "",
      activeSearch: "",
      activeGrade: "" as GradeFilter,
      gradeFilters: [
        { value: "" as GradeFilter, label: "All" },
        { value: "hot" as GradeFilter, label: "Hot" },
        { value: "warm" as GradeFilter, label: "Warm" },
        { value: "cold" as GradeFilter, label: "Cold" }
      ],
      prospects: [] as Prospect[],
      selectedProspectId: "",
      messages: [] as InboxMessage[],
      newMessageText: "",
      pendingAttachment: null as ChatAttachmentDraft | null,
      uploadingAttachment: false,
      inboxSocket: null as InboxSocketHandle | null
    };
  },
  computed: {
    selectedProspect(): Prospect | null {
      return this.prospects.find((prospect) => prospect.id === this.selectedProspectId) || null;
    },
    emptyStateMessage(): string {
      if (this.activeSearch && this.activeGrade) {
        return "No prospects match your search and score filter.";
      }
      if (this.activeSearch) {
        return "No prospects match your search.";
      }
      if (this.activeGrade) {
        return "No prospects match this score filter.";
      }
      return "No prospects yet.";
    },
    canSendMessage(): boolean {
      const hasText = Boolean(this.newMessageText.trim());
      const hasReadyAttachment = this.pendingAttachment?.status === "ready";
      return hasText || hasReadyAttachment;
    }
  },
  watch: {
    hasGhlLocation: {
      immediate: false,
      handler(value: boolean) {
        if (value) {
          this.bootstrapInbox();
        } else {
          this.disconnectSocket();
        }
      }
    }
  },
  mounted() {
    if (this.hasGhlLocation) {
      this.bootstrapInbox();
    } else {
      this.loading = false;
    }
  },
  beforeUnmount() {
    this.disconnectSocket();
  },
  methods: {
    messageKey(message: InboxMessage) {
      return buildMessageKey(message);
    },
    isAiActive(prospect: Prospect) {
      return !prospect.humanHandoff && prospect.aiEnabled !== false;
    },
    formatName(prospect: Prospect) {
      const name = [prospect.firstName, prospect.lastName].filter(Boolean).join(" ").trim();
      return name || "Unknown";
    },
    ghlContactUrl(prospect: Prospect) {
      return buildGhlContactUrl(this.locationId, prospect.id);
    },
    hasSummary(prospect: Prospect) {
      return Boolean(prospect.conversationSummary?.trim());
    },
    formatGrade(grade?: string) {
      const value = String(grade || "cold").toLowerCase();
      if (value === "hot") return "Hot";
      if (value === "warm") return "Warm";
      return "Cold";
    },
    gradeBadgeClass(grade?: string) {
      const value = String(grade || "cold").toLowerCase();
      if (value === "hot") return "badge-light-danger";
      if (value === "warm") return "badge-light-warning";
      return "badge-light-secondary";
    },
    scoreTooltip(prospect: Prospect) {
      const reasons = prospect.scoreBreakdown?.reasons || [];
      if (!reasons.length) {
        return "No score breakdown yet.";
      }
      return reasons.join(", ");
    },
    async bootstrapInbox() {
      await this.loadProspects();
      this.connectSocket();
    },
    connectSocket() {
      this.disconnectSocket();
      this.inboxSocket = createInboxSocket({
        onMessage: (payload) => this.handleSocketEvent(payload)
      });
    },
    disconnectSocket() {
      if (this.inboxSocket) {
        this.inboxSocket.close();
        this.inboxSocket = null;
      }
    },
    handleSocketEvent(payload: Record<string, unknown>) {
      if (payload?.type !== "message" || !payload.contactId) {
        return;
      }

      const contactId = String(payload.contactId);
      const prospect = this.prospects.find((item) => item.id === contactId);
      const incomingMessage = payload.message as InboxMessage | null;

      if (prospect) {
        if (incomingMessage?.content) {
          prospect.lastMessage = incomingMessage.content;
          prospect.lastMessageAt = incomingMessage.createdAt || String(payload.lastMessageAt || "");
          prospect.lastMessageRole = incomingMessage.role;
        } else if (payload.preview) {
          prospect.lastMessage = String(payload.preview);
          prospect.lastMessageAt = String(payload.lastMessageAt || prospect.lastMessageAt || "");
        }

        if (payload.humanHandoff != null) {
          prospect.humanHandoff = Boolean(payload.humanHandoff);
        }
        if (payload.aiEnabled != null) {
          prospect.aiEnabled = Boolean(payload.aiEnabled);
        }
        if (payload.messageChannel) {
          prospect.messageChannel = String(payload.messageChannel);
        }

        this.reorderProspects(contactId);
      }

      if (contactId === this.selectedProspectId && incomingMessage) {
        this.appendMessage(incomingMessage);
        this.scrollToBottom();
      }
    },
    reorderProspects(contactId: string) {
      const index = this.prospects.findIndex((prospect) => prospect.id === contactId);
      if (index <= 0) {
        return;
      }
      const [prospect] = this.prospects.splice(index, 1);
      this.prospects.unshift(prospect);
    },
    appendMessage(message: InboxMessage) {
      const key = this.messageKey(message);
      const existingIndex = this.messages.findIndex((item) => this.messageKey(item) === key);
      if (existingIndex >= 0) {
        this.messages[existingIndex] = {
          ...this.messages[existingIndex],
          ...message
        };
        return;
      }
      this.messages.push(message);
    },
    scrollToBottom() {
      const scroll = () => {
        const container = this.$refs.messagesRef as HTMLElement | undefined;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      };

      this.$nextTick(() => {
        scroll();
        this.$nextTick(scroll);
        requestAnimationFrame(scroll);
      });
    },
    async selectProspect(prospect: Prospect) {
      if (this.selectedProspectId === prospect.id) {
        return;
      }

      this.selectedProspectId = prospect.id;
      this.messages = [];
      this.clearAttachment();
      this.threadError = "";
      this.newMessageText = "";
      await this.loadThread(prospect.id);
    },
    async loadThread(contactId: string) {
      this.threadLoading = true;
      this.threadError = "";

      try {
        const response = await listConversationMessages(contactId, { limit: 100 });
        this.messages = response.data?.messages || [];
      } catch (err) {
        this.threadError = err?.response?.data?.message || err?.message || "Unable to load messages.";
      } finally {
        this.threadLoading = false;
        this.scrollToBottom();
      }
    },
    setGradeFilter(grade: GradeFilter) {
      if (this.activeGrade === grade) {
        return;
      }
      this.activeGrade = grade;
      this.loadProspects();
    },
    showSummary(prospect: Prospect) {
      const summary = prospect.conversationSummary?.trim();
      if (!summary) {
        return;
      }

      const generatedAt = prospect.summaryGeneratedAt
        ? new Date(prospect.summaryGeneratedAt).toLocaleString()
        : null;

      Swal.fire({
        title: `Conversation summary — ${this.formatName(prospect)}`,
        html: generatedAt
          ? `<div class="text-muted fs-7 mb-3">Generated ${generatedAt}</div><div class="text-start">${escapeHtml(summary).replace(/\n/g, "<br>")}</div>`
          : `<div class="text-start">${escapeHtml(summary).replace(/\n/g, "<br>")}</div>`,
        confirmButtonText: "Close",
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
    },
    async loadProspects({ sync = false, preserveSelection = true } = {}) {
      if (!this.hasGhlLocation) {
        this.loading = false;
        return;
      }

      const previousSelection = preserveSelection ? this.selectedProspectId : "";
      const isInitialLoad = this.loading;
      if (!isInitialLoad) {
        if (sync) {
          this.syncing = true;
        } else {
          this.searching = true;
        }
      }

      this.loadError = "";

      try {
        const params: Record<string, string | number | boolean> = {
          limit: 50,
          offset: 0
        };
        if (this.activeSearch) {
          params.q = this.activeSearch;
        }
        if (this.activeGrade) {
          params.grade = this.activeGrade;
        }
        if (sync) {
          params.sync = true;
        }

        const response = await listProspects(params);
        this.prospects = response.data?.pageList || [];

        if (previousSelection && this.prospects.some((prospect) => prospect.id === previousSelection)) {
          this.selectedProspectId = previousSelection;
        } else if (!this.selectedProspectId && this.prospects.length) {
          this.selectedProspectId = this.prospects[0].id;
          await this.loadThread(this.selectedProspectId);
        } else if (this.selectedProspectId && !this.prospects.some((prospect) => prospect.id === this.selectedProspectId)) {
          this.selectedProspectId = this.prospects[0]?.id || "";
          this.messages = [];
          if (this.selectedProspectId) {
            await this.loadThread(this.selectedProspectId);
          }
        }
      } catch (err) {
        this.loadError = err?.response?.data?.message || err?.message || "Unable to load prospects.";
      } finally {
        this.loading = false;
        this.searching = false;
        this.syncing = false;
      }
    },
    search() {
      this.activeSearch = this.searchQuery.trim();
      this.selectedProspectId = "";
      this.messages = [];
      this.loadProspects({ preserveSelection: false });
    },
    clearSearch() {
      this.searchQuery = "";
      this.activeSearch = "";
      this.selectedProspectId = "";
      this.messages = [];
      this.loadProspects({ preserveSelection: false });
    },
    refreshFromCrm() {
      this.loadProspects({ sync: true });
    },
    openAttachmentPicker() {
      const input = this.$refs.attachmentInputRef as HTMLInputElement | undefined;
      input?.click();
    },
    clearAttachment() {
      this.pendingAttachment = null;
      const input = this.$refs.attachmentInputRef as HTMLInputElement | undefined;
      if (input) {
        input.value = "";
      }
    },
    async handleAttachmentSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      this.uploadingAttachment = true;
      this.pendingAttachment = {
        id: 0,
        originalFilename: file.name,
        mimeType: file.type,
        publicUrl: null,
        status: "pending"
      };

      try {
        const response = await uploadChatMedia(file);
        const uploaded = response.data;
        this.pendingAttachment = {
          id: Number(uploaded.id),
          originalFilename: uploaded.originalFilename || file.name,
          mimeType: uploaded.mimeType || file.type,
          publicUrl: uploaded.publicUrl || null,
          status: uploaded.status || "pending"
        };

        if (this.pendingAttachment.status !== "ready") {
          await this.pollAttachmentReady(this.pendingAttachment.id);
        }
      } catch (err) {
        this.clearAttachment();
        Swal.fire({
          text: (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
            || (err as Error)?.message
            || "Failed to upload attachment.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      } finally {
        this.uploadingAttachment = false;
      }
    },
    async pollAttachmentReady(mediaId: number, attempts = 30) {
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        const response = await getContentMediaStatus(mediaId);
        const media = response.data;
        if (media?.status === "ready") {
          this.pendingAttachment = {
            id: Number(media.id),
            originalFilename: media.originalFilename,
            mimeType: media.mimeType,
            publicUrl: media.publicUrl || null,
            status: "ready"
          };
          return;
        }
        if (media?.status === "failed") {
          throw new Error("Attachment upload failed");
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      throw new Error("Attachment upload timed out");
    },
    async sendMessage() {
      const prospect = this.selectedProspect;
      const text = this.newMessageText.trim();
      const mediaIds = this.pendingAttachment?.status === "ready" && this.pendingAttachment.id
        ? [this.pendingAttachment.id]
        : [];

      if (!prospect || (!text && !mediaIds.length) || this.sendingMessage || this.uploadingAttachment) {
        return;
      }

      const optimisticContent = text || this.pendingAttachment?.originalFilename || "Attachment";
      const optimisticMessage: InboxMessage = {
        id: `temp-${Date.now()}`,
        role: "human",
        content: optimisticContent,
        attachments: this.pendingAttachment?.publicUrl
          ? [{
              url: this.pendingAttachment.publicUrl,
              mimeType: this.pendingAttachment.mimeType,
              filename: this.pendingAttachment.originalFilename
            }]
          : [],
        createdAt: new Date().toISOString()
      };

      this.appendMessage(optimisticMessage);
      this.newMessageText = "";
      const attachmentDraft = this.pendingAttachment;
      this.clearAttachment();
      this.sendingMessage = true;
      this.scrollToBottom();

      try {
        const response = await sendConversationMessage(prospect.id, {
          message: text,
          mediaIds
        });
        const saved = response.data?.message as InboxMessage | undefined;
        if (saved) {
          this.messages = this.messages.filter((item) => item !== optimisticMessage);
          this.appendMessage(saved);
        }

        prospect.humanHandoff = Boolean(response.data?.humanHandoff);
        prospect.aiEnabled = response.data?.aiEnabled !== false;
        prospect.lastMessage = saved?.content || optimisticContent;
        prospect.lastMessageAt = saved?.createdAt || optimisticMessage.createdAt || "";
        prospect.lastMessageRole = "human";
        this.reorderProspects(prospect.id);
        this.scrollToBottom();
      } catch (err) {
        this.messages = this.messages.filter((item) => item !== optimisticMessage);
        this.newMessageText = text;
        if (attachmentDraft) {
          this.pendingAttachment = attachmentDraft;
        }
        Swal.fire({
          text: err?.response?.data?.message || err?.message || "Failed to send message.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      } finally {
        this.sendingMessage = false;
      }
    },
    async resumeAi(prospect: Prospect) {
      if (this.isAiActive(prospect) || this.resumingId) {
        return;
      }

      this.resumingId = prospect.id;

      try {
        await resumeGhlAiConversation({ contactId: prospect.id });
        prospect.humanHandoff = false;
        prospect.aiEnabled = true;
      } catch (err) {
        Swal.fire({
          text: err?.response?.data?.message || err?.message || "Failed to re-enable AI for this prospect.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      } finally {
        this.resumingId = "";
      }
    }
  }
});
